import { computed, inject, Injectable, signal } from '@angular/core';
import { finalize, map, Observable, of, tap } from 'rxjs';
import { LoaderService, Pagination, PatientAppStatusCounts, PatientsAppStatus, SearchPatientResult, SearchResult, TspDashboardRepository } from '../core';
import { BaseService } from './base.service';


/**
 * This service handles dashboard page data.
 */
@Injectable({ providedIn: 'root' })
export class DashboardService extends BaseService {

	private readonly loaderSrv = inject(LoaderService);
	private readonly dashbrdRepo = inject(TspDashboardRepository);

	appStatus = signal<PatientsAppStatus | null>(null);

	private readonly DEFAULT_FILTERS: Pagination = { page: 1, top: 10 };

	appActivations = computed(() => this._statusValue(this.appStatus()?.activations));
	appSyncs = computed(() => this._statusValue(this.appStatus()?.prescriptionsSync));
	appAdjustments = computed(() => this._statusValue(this.appStatus()?.strutsAdjustments));
	selectedChart = signal<ChartItemEnum | null>(null);
	items = signal<SearchPatientResult[]>([]);
	itemsSize = signal<number>(0);
	filters = signal<Pagination>(this.DEFAULT_FILTERS);

	private readonly EMPTY_RESULT: SearchResult<SearchPatientResult> = { items: [], count: 0 };


	/**
	 * Init dashboard data.
	 */
	init(): void {
		this.loaderSrv.show();
		this._getAppStatus().pipe(
			finalize(() => this.loaderSrv.hide())
		).subscribe(status => this.appStatus.set(status));
	}

	private _statusValue(data?: PatientAppStatusCounts): number[] {
		return data ? [data.activated, data.notActivated] : [0, 0];
	}

	/**
	 * Get app status overview.
	 */
	private _getAppStatus(): Observable<PatientsAppStatus> {
		return this.dashbrdRepo.patientAppStatusCounts().pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Update patient app status list.
	 */
	updateList(): Observable<boolean> {
		this.loaderSrv.show();
		const chart = this.selectedChart();
		if (!chart) return of(false);
		return this._appStatusList(chart).pipe(
			finalize(() => this.loaderSrv.hide()),
			tap(res => this._updateList(res)),
			map(() => true)
		);
	}

	private _updateList(result: SearchResult<SearchPatientResult> | null): void {
		this.items.set(result?.items ?? []);
		this.itemsSize.set(result?.count ?? 0);
	}

	/**
	 * Get app status list according to selected chart.
	 */
	private _appStatusList(chart: ChartItemEnum): Observable<(SearchResult<SearchPatientResult> | null)> {
		switch (chart) {
			case ChartItemEnum.ACTIVATED: return this._appActivationList(true, this.filters());
			case ChartItemEnum.ACTIVATED_NOT: return this._appActivationList(false, this.filters());
			case ChartItemEnum.SYNC: return this._prescriptionSyncList(true, this.filters());
			case ChartItemEnum.SYNC_NOT: return this._prescriptionSyncList(false, this.filters());
			case ChartItemEnum.SCHEDULE: return this._strutsAdjustmentStatus(true, this.filters());
			case ChartItemEnum.SCHEDULE_NOT: return this._strutsAdjustmentStatus(false, this.filters());
			default: return of(this.EMPTY_RESULT);
		}
	}

	/**
	 * Get app activation status list.
	 */
	private _appActivationList(status: boolean, filters: Pagination): Observable<(SearchResult<SearchPatientResult> | null)> {
		return this.dashbrdRepo.patientAppActivationStatus(status, filters).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Get prescription sync status list.
	 */
	private _prescriptionSyncList(status: boolean, filters: Pagination): Observable<SearchResult<SearchPatientResult>> {
		return this.dashbrdRepo.patientAppPrescriptionStatus(status, filters).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Get struts adjustment status list.
	 */
	private _strutsAdjustmentStatus(status: boolean, filters: Pagination): Observable<SearchResult<SearchPatientResult>> {
		return this.dashbrdRepo.patientAppStrutAdjustStatus(status, filters).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Reset dashboard data.
	 */
	reset(): void {
		this.appStatus.set(null);
		this.selectedChart.set(null)
		this.items.set([]);
		this.itemsSize.set(0);
		this.filters.set(this.DEFAULT_FILTERS);
	}
}


export enum ChartItemEnum {
	ACTIVATED = 'Activated',
	ACTIVATED_NOT = 'NotActivated',
	SYNC = 'Sync',
	SYNC_NOT = 'NotSync',
	SCHEDULE = 'Schedule',
	SCHEDULE_NOT = 'NotSchedule'
}